import React from 'react';
import { useAssessmentData } from './AssessmentDataContext';
import { SectionHeader, ScrollTable, BucketPill, DeltaBadge, rtrColor, corrColor, ASSESSMENT_PERIODS } from './shared';

// Extracted verbatim from Assessment.js's RtrWinRateTable + RtrPearsonTable
// components and the `activeTab === 'pearson'` render block (Tech Debt
// 2026-09, "Assessment and Backtest are god files" -- remaining-7-tabs
// context lift). Reads shared state from useAssessmentData() instead of via
// props.

function RtrWinRateTable({ buckets, prevByBucket = {} }) {
  const displayPeriods = ASSESSMENT_PERIODS;
  return (
    <ScrollTable minWidth={520}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08]">
            <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-20">Bucket</th>
            <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-12">N</th>
            {displayPeriods.map(p => (
              <th key={p} className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[64px]">{p}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buckets.map((row) => {
            const prev = prevByBucket[row.bucket];
            return (
              <tr
                key={row.bucket}
                className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors"
              >
                <td className="py-2 px-3"><BucketPill bucket={row.bucket} /></td>
                <td className="py-2 px-2 text-right text-gray-500 font-mono tabular-nums text-[11px] whitespace-nowrap">
                  {row.sample_count}
                  <DeltaBadge curr={row.sample_count} prev={prev?.sample_count} decimals={0} suffix="" />
                </td>
                {displayPeriods.map(p => {
                  const v = row.rtr_win_rate?.[p];
                  return (
                    <td key={p} className={`py-2 px-2 text-right font-mono tabular-nums text-[12px] whitespace-nowrap ${rtrColor(v)}`}>
                      {v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '—'}
                      <DeltaBadge curr={v} prev={prev?.rtr_win_rate?.[p]} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollTable>
  );
}

// ── Cross-bucket RTR Pearson table ────────────────────────────────────────────


function RtrPearsonTable({ rtrCorrelations, prevCorrelations = null }) {
  const displayPeriods = ASSESSMENT_PERIODS;
  const sides = [
    { key: 'high', label: 'Calls (HIGH)', sublabel: 'bucket score vs RTR win%', color: 'text-trading-green-300' },
    { key: 'low',  label: 'Puts (LOW)',   sublabel: 'put strength vs RTR win%',  color: 'text-trading-red-300' },
  ];
  return (
    <ScrollTable minWidth={480}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.08]">
            <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 w-32">Side</th>
            {displayPeriods.map(p => (
              <th key={p} className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600 min-w-[64px]">{p}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sides.map(({ key, label, sublabel, color }) => (
            <tr key={key} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
              <td className="py-2 px-3">
                <span className={`text-[12px] font-medium ${color}`}>{label}</span>
                <span className="block text-[11px] text-gray-600 mt-0.5">{sublabel}</span>
              </td>
              {displayPeriods.map(p => {
                const v = rtrCorrelations?.[p]?.[key];
                const pv = prevCorrelations?.[p]?.[key];
                return (
                  <td key={p} className={`py-2 px-2 text-right font-mono tabular-nums text-[12px] whitespace-nowrap ${corrColor(v)}`}>
                    {v != null ? (v >= 0 ? `+${v.toFixed(3)}` : v.toFixed(3)) : '—'}
                    <DeltaBadge curr={v} prev={pv} decimals={3} suffix="" />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollTable>
  );
}

export default function PearsonTab() {
  const {
    data, selectedWindowBuckets, prevByBucket, selectedWindow,
    selectedRtrCorrelations, activeRtrCorrelations,
  } = useAssessmentData();
  const { version } = data;
  return (
  <>
    <SectionHeader>
      RTR win rates — relative to 71% random baseline
      {selectedWindow ? ` — ${selectedWindow} window` : ''}
    </SectionHeader>
    <p className="text-[12px] text-gray-400 mb-4 leading-relaxed">
      RTR win% = <span className="font-mono text-gray-200 tabular-nums">(win% − floor) / (100% − floor)</span>.
      Zero = matches a random walk. Positive = beating random. Negative = worse than random.
      Floor is side-specific — derived from gambler's ruin <span className="font-mono text-gray-300 tabular-nums">P = M/(K+M)</span>:{' '}
      <span className="text-trading-green-300 font-mono tabular-nums">calls 71.4%</span> (K=2σ/M=5σ),{' '}
      <span className="text-trading-red-300 font-mono tabular-nums">puts 66.7%</span> (K=1σ/M=2σ).
    </p>
    {selectedWindowBuckets.length === 0
      ? <p className="text-gray-500 text-sm text-center py-8">No buckets to display.</p>
      : <RtrWinRateTable buckets={selectedWindowBuckets} prevByBucket={prevByBucket} />
    }

    <div className="mt-6">
      <SectionHeader>Cross-bucket RTR Pearson</SectionHeader>
      <p className="text-[12px] text-gray-400 mb-4 leading-relaxed">
        Pearson correlation of <span className="text-gray-200">bucket threshold score</span> vs <span className="text-gray-200">RTR win%</span> across the 7 call buckets and 3 put buckets.
        Computed from existing stored data — no reassessment needed.
        Positive = higher-scored buckets beat random more than lower-scored ones.
        More separable than per-trade Pearson because trade-level noise is averaged out before correlating.
      </p>
      <RtrPearsonTable
        rtrCorrelations={selectedRtrCorrelations || version.rtr_correlations}
        prevCorrelations={activeRtrCorrelations}
      />
    </div>

  </>
  );
}
